/**
 * What the models believe about you (SYR-111 v3, Group B).
 *
 * The opinion statements: quality, trust, value, uniqueness, whether it makes
 * sense to pick you over a brand that is the same. The models hold two sets
 * of these beliefs, what they learned in training and what they find when
 * they search, and the gap between the two is the record problem. They also
 * estimate what your customers would say, and when a survey exists the real
 * customer line sits beside that estimate.
 *
 * Deliberately NOT a comparison between brands: on these statements the three
 * models disagree with each other (+0.25 cross-model agreement), so a ranking
 * would be noise dressed as a finding. Competitors are shown per model, on
 * request, as colour.
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Space, Table, Tag, Tooltip, Typography } from 'antd';
import { Card } from '../../atoms';
import { ReadSection } from './ReadSection';
import { InfoTip } from './InfoTip';
import { THEME } from '../../config/theme';
import { getDisplayName } from '../../../../utils/elementDisplayNames';
import { IModelBeliefsPageReading, IModelBeliefsStatementRead } from '../../../../services/nestjs-base-api';
import { StatementDetail } from './StatementDetail';
import {
  CellReading,
  anchorFor,
  deltaColour,
  deltaInBrandTerms,
  rawDelta,
  fmt,
  readCell,
  readPages,
  reasonLabel,
  signed,
} from './beliefCells';

const { Text, Paragraph } = Typography;

interface Props {
  brandName: string;
  statements: IModelBeliefsStatementRead[];
  hasSurvey: boolean;
  // The synthesis's paragraph on this table, when a synthesis has run.
  note?: string | null;
  // v4: the searched view is what the pages say; the models' guess at the
  // customers and the per-model chips then come from memory.
  pagesMode?: boolean;
  pagesRead?: number;
  // v4: every found page reading, keyed by statement code.
  pageReadings?: Record<string, IModelBeliefsPageReading[]>;
  n?: string;
}

interface Row {
  key: string;
  text: string;
  reasons: { label: string; text: string }[];
  readings: IModelBeliefsPageReading[];
  category: string;
  scaleMax: number;
  higherIsBetter: boolean;
  memory: ReturnType<typeof readCell>;
  searched: ReturnType<typeof readCell>;
  customerEstimate: ReturnType<typeof readCell>;
  survey: { mean: number; n: number } | null;
  byModel: ModelChip[];
  competitors: { name: string; byModel: ModelChip[] }[];
}

// One model's own score, or the reason it has none. A model that declined to
// hold the view ("as an AI I do not buy bedding") says so on its chip, so a
// line with one model behind it does not read as three.
interface ModelChip {
  label: string;
  value: number | null;
  reason: CellReading['reason'];
}

const chipText = (m: ModelChip): string =>
  m.value === null ? reasonLabel(m.reason).toLowerCase() : fmt(m.value);

const num = (reading: ReturnType<typeof readCell>, scaleMax: number) =>
  reading.value === null ? (
    <Text style={{ color: THEME.textMuted, fontSize: 12 }}>{reasonLabel(reading.reason)}</Text>
  ) : (
    <Tooltip title={`${anchorFor(reading.value, scaleMax)} · ${reading.n} answers`}>
      <Text>{fmt(reading.value)}</Text>
    </Tooltip>
  );

export const OpinionBeliefs: React.FunctionComponent<Props> = ({
  brandName,
  statements,
  hasSurvey,
  note,
  pagesMode = false,
  pagesRead = 0,
  pageReadings,
  n = '3',
}) => {
  const intl = useIntl();
  const opinions = statements.filter((s) => s.group === 'B');
  if (opinions.length === 0) return null;

  const rows: Row[] = opinions.map((s) => ({
    key: s.code,
    text: s.text,
    reasons: s.reasons ?? [],
    readings: pageReadings?.[s.code] ?? [],
    category: s.category,
    scaleMax: s.scaleMax,
    higherIsBetter: s.higherIsBetter,
    memory: readCell(s.brand.memory, 'self'),
    searched: pagesMode
      ? (() => {
          const pages = readPages(s.brand.pages);
          return {
            value: pages.value,
            reason: (pages.value === null ? 'no_data' : null) as CellReading['reason'],
            n: pages.pages,
          };
        })()
      : readCell(s.brand.searched, 'self'),
    customerEstimate: readCell(pagesMode ? s.brand.memory : s.brand.searched, 'customer'),
    survey: s.survey,
    byModel: s.byModel.map((m) => {
      const cell = readCell(pagesMode ? m.memory : m.searched, 'self');
      return { label: m.label, value: cell.value, reason: cell.reason };
    }),
    competitors: s.competitors.map((c) => ({
      name: c.name,
      byModel: c.byModel.map((m) => {
        const cell = readCell(pagesMode ? m.memory : m.searched, 'self');
        return { label: m.label, value: cell.value, reason: cell.reason };
      }),
    })),
  }));

  // Sorted so the lines where searching hurt most come first: that is where
  // a record fix lives.
  const sorted = [...rows].sort((a, b) => {
    const da = deltaInBrandTerms(a.searched.value, a.memory.value, a.higherIsBetter) ?? 0;
    const db = deltaInBrandTerms(b.searched.value, b.memory.value, b.higherIsBetter) ?? 0;
    return da - db;
  });


  // The beliefs a record fix cannot reach: unfavourable, and the same before
  // and after the models searched. Nothing on the web put them there, so
  // nothing on the web takes them away. With no survey this is the one place
  // the page says plainly that only the customers can settle it; with a
  // survey the customer column already does.
  // In a pages read an unfavourable belief the pages agree with IS on the
  // record, so the box does not apply.
  const heldBeforeSearching = hasSurvey || pagesMode
    ? []
    : sorted.filter((r) => {
        if (r.searched.value === null || r.memory.value === null) return false;
        const after = r.higherIsBetter ? r.searched.value : r.scaleMax + 1 - r.searched.value;
        const delta = deltaInBrandTerms(r.searched.value, r.memory.value, r.higherIsBetter) ?? 0;
        return after < 4.5 && Math.abs(delta) < 0.3;
      });

  const pagesSub = intl.formatMessage(
    {
      id: hasSurvey ? 'marketerView.modelBeliefs.beliefs.pagesSubSurvey' : 'marketerView.modelBeliefs.beliefs.pagesSub',
      defaultMessage: hasSurvey
        ? 'Opinions a model cannot look up, such as quality, trust, value and uniqueness, rated 1 (strongly disagree) to 7 (strongly agree). From memory is what the models believe about {brand} without looking anything up. The pages say is what the pages the models read say on the same statement, scored page by page; the small number is the difference, red where the pages say less for you than the models remember. Their guess at your customers is what the models think your customers would say, and your customers is what they actually said in your survey, so the gap between the two is where the models are wrong about you.'
        : 'Opinions a model cannot look up, such as quality, trust, value and uniqueness, rated 1 (strongly disagree) to 7 (strongly agree). From memory is what the models believe about {brand} without looking anything up. The pages say is what the pages the models read say on the same statement, scored page by page; the small number is the difference, red where the pages say less for you than the models remember. Their guess at your customers is what the models think your customers would say; only your customers can say whether that guess is right.',
    },
    { brand: brandName },
  );

  const sub = pagesMode ? pagesSub : (
    <>
      Opinions a model cannot look up, such as quality, trust, value and uniqueness, rated 1
      (strongly disagree) to 7 (strongly agree). From memory is what the models believed about{' '}
      {brandName} before looking anything up. After searching is their answer once they had read the
      web, and the small number is the difference: where it is red, the web made the models think
      less of you. Their guess at your customers is what the models think your customers would say.
      {hasSurvey
        ? ' Your customers is what your customers actually said in your survey, so the gap between the two is where the models are wrong about you.'
        : ' Only your customers can say whether that guess is right; the survey built from these statements is ready to send.'}
      {' '}Rows are sorted so the beliefs the web changed most are at the top.{' '}
      <InfoTip title="Each score's tooltip shows the scale wording it sits at (for example “agree”) and how many answers it averages.">
        Hover any score for its wording and how many answers are behind it.
      </InfoTip>
    </>
  );

  return (
    <ReadSection id="beliefs" n={n} title="What do the models believe about you?" sub={sub}>
      <Card>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {note && (
          <Paragraph style={{ marginBottom: 0, maxWidth: '72ch', fontSize: 14.5, lineHeight: 1.6 }}>{note}</Paragraph>
        )}

        {heldBeforeSearching.length > 0 && (
          <div
            style={{
              border: `1px solid ${THEME.border}`,
              background: THEME.bgChip,
              borderRadius: 12,
              padding: '14px 18px',
              maxWidth: '72ch',
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: THEME.textBase, marginBottom: 6 }}>
              <FormattedMessage
                id="marketerView.modelBeliefs.heldBefore.title"
                defaultMessage="Not a record problem"
              />
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.55, color: THEME.textSecondary, marginBottom: 10 }}>
              <FormattedMessage
                id="marketerView.modelBeliefs.heldBefore.body"
                defaultMessage="{count, plural, one {One unfavourable belief did not change} other {{count} unfavourable beliefs did not change}} when the models read the web. They held {count, plural, one {it} other {these}} before they searched, so the record is not the cause and no record fix will shift {count, plural, one {it} other {them}}. Only your customers can tell you whether {count, plural, one {it is} other {they are}} true."
                values={{ count: heldBeforeSearching.length }}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {heldBeforeSearching.map((r) => (
                <Tooltip key={r.key} title={`${getDisplayName(r.category)} · from memory ${fmt(r.memory.value)}, after searching ${fmt(r.searched.value)}`}>
                  <span
                    style={{
                      fontSize: 12.5,
                      color: THEME.textBase,
                      background: '#fff',
                      border: `1px solid ${THEME.border}`,
                      borderRadius: 999,
                      padding: '3px 10px',
                    }}
                  >
                    {r.text} · {fmt(r.searched.value)}
                  </span>
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        <Table
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          dataSource={sorted}
          expandable={{
            rowExpandable: (row: Row) =>
              row.reasons.length > 0 || row.readings.length > 0 || pagesMode || row.competitors.length > 0,
            expandedRowRender: (row: Row) => (
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <StatementDetail reasons={row.reasons} readings={row.readings} pagesMode={pagesMode} />
                {row.competitors.length > 0 && (
                  <Space direction="vertical" size={4}>
                    <Text style={{ color: THEME.textSecondary, fontSize: 12 }}>
                      Competitors on this line, per model. The models disagree with each
                      other on opinions, so this is colour, not a ranking.
                    </Text>
                    {row.competitors.map((c) => (
                      <Space key={c.name} size={8}>
                        <Text style={{ fontSize: 13, minWidth: 120, display: 'inline-block' }}>
                          {c.name}
                        </Text>
                        {c.byModel.map((m) => (
                          <Tag key={m.label} style={{ margin: 0 }}>
                            {m.label} {chipText(m)}
                          </Tag>
                        ))}
                      </Space>
                    ))}
                  </Space>
                )}
              </Space>
            ),
          }}
          columns={[
            {
              title: 'Statement',
              dataIndex: 'text',
              width: 260,
              render: (text: string, row: Row) => (
                <Space direction="vertical" size={0}>
                  <Text style={{ fontSize: 13 }}>{text}</Text>
                  <Text style={{ color: THEME.textMuted, fontSize: 11 }}>
                    {getDisplayName(row.category)}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'From memory',
              key: 'memory',
              width: 110,
              render: (_: unknown, row: Row) => num(row.memory, row.scaleMax),
            },
            {
              title: pagesMode
                ? intl.formatMessage({ id: 'marketerView.modelBeliefs.pages.column', defaultMessage: 'The pages say' })
                : 'After searching',
              key: 'searched',
              width: 120,
              render: (_: unknown, row: Row) => {
                const delta = deltaInBrandTerms(row.searched.value, row.memory.value, row.higherIsBetter);
                if (pagesMode && row.searched.value === null) {
                  return (
                    <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
                      {intl.formatMessage({ id: 'marketerView.modelBeliefs.pages.nothing', defaultMessage: 'Nothing' })}
                    </Text>
                  );
                }
                return (
                  <Space size={6}>
                    {pagesMode ? (
                      <Tooltip
                        title={intl.formatMessage(
                          {
                            id: 'marketerView.modelBeliefs.pages.cellTip',
                            defaultMessage: '{covering} of {read} pages read speak to this',
                          },
                          { covering: row.searched.n, read: pagesRead },
                        )}
                      >
                        <Text strong>{fmt(row.searched.value)}</Text>
                      </Tooltip>
                    ) : (
                      <Text strong>{row.searched.value === null ? '' : fmt(row.searched.value)}</Text>
                    )}
                    {!pagesMode && row.searched.value === null && num(row.searched, row.scaleMax)}
                    {delta !== null && Math.abs(delta) >= 0.3 && (
                      <Text style={{ color: deltaColour(delta), fontSize: 12 }}>
                        {signed(rawDelta(row.searched.value, row.memory.value))}
                      </Text>
                    )}
                  </Space>
                );
              },
            },
            {
              title: (
                <InfoTip title="What the models think a typical customer of yours would answer to the same statement.">
                  Their guess at your customers
                </InfoTip>
              ),
              key: 'estimate',
              width: 150,
              render: (_: unknown, row: Row) => num(row.customerEstimate, row.scaleMax),
            },
            {
              title: (
                <InfoTip title="Your customers’ own answers to the same statement, from your survey. Hover a score for the number of responses behind it.">
                  Your customers
                </InfoTip>
              ),
              key: 'survey',
              width: 130,
              render: (_: unknown, row: Row) =>
                row.survey ? (
                  <Tooltip title={`${row.survey.n} responses`}>
                    <Text strong style={{ color: THEME.primaryDark }}>
                      {fmt(row.survey.mean)}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text style={{ color: THEME.textMuted, fontSize: 12 }}>
                    {hasSurvey ? '—' : 'Ask them'}
                  </Text>
                ),
            },
            {
              title: 'By model',
              key: 'models',
              width: 220,
              render: (_: unknown, row: Row) => (
                <Space size={4} wrap>
                  {row.byModel.map((m) => (
                    <Tag key={m.label} style={{ margin: 0 }}>
                      {m.label} {chipText(m)}
                    </Tag>
                  ))}
                </Space>
              ),
            },
          ]}
        />

        <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, fontSize: 13, maxWidth: '72ch' }}>
          {pagesMode ? intl.formatMessage({
            id: 'marketerView.modelBeliefs.beliefs.pagesFoot',
            defaultMessage:
              'Sorted with the lines where the pages say least for you, against what the models remember, at the top. Scores are 1 to 7. Not compared against competitors on purpose: the three models disagree with each other on opinions, so expand a line for colour and never for a ranking.',
          }) : <>Sorted with the lines searching hurt most at the top. Scores are 1 to 7 in the
          models’ own view; the small number is the move from memory to searched, in your
          favour (green) or against you (red). Not compared against competitors on purpose:
          the three models disagree with each other on opinions, so expand a line for colour
          and never for a ranking.</>}
        </Paragraph>
      </Space>
      </Card>
    </ReadSection>
  );
};
